import type { Poem } from '../types'
import { schoolPoems, schoolPoemTextKey } from './schoolPoems'
import { expandedPoems } from './expandedPoems'

const sourceUrl = 'https://github.com/chinese-poetry/chinese-poetry'

type UndatedPoem = Omit<Poem, 'datePrecision' | 'dateEvidence'>
type PoemChronology = Pick<
  Poem,
  'year' | 'yearLabel' | 'eraLabel' | 'datePrecision' | 'dateEvidence'
>

const chronology = (
  year: number,
  yearLabel: string,
  eraLabel: string,
  datePrecision: Poem['datePrecision'],
  dateEvidence: string,
): PoemChronology => ({ year, yearLabel, eraLabel, datePrecision, dateEvidence })

const curatedPoemChronologies: Record<string, PoemChronology> = {
  'jing-ke-yishui-song': chronology(-227, '前227年', '燕王喜二十八年 · 易水送别', 'exact', '《战国策·燕策三》将易水送别置于荆轲西行刺秦王的当年，即秦王政二十年、前227年。'),
  'liu-bang-da-feng-ge': chronology(-195, '前195年', '汉高祖十二年 · 还乡沛县', 'exact', '《史记》《汉书》均记刘邦平定英布后于高祖十二年还乡置酒，在沛宫击筑作歌。'),
  'han-yuefu-shang-ye': chronology(50, '约1世纪', '东汉乐府歌辞', 'period', '《上邪》收入《鼓吹曲辞》，现存传本没有作者、题序或历史本事，只能约定在汉代乐府传统中。'),
  'cao-zhi-seven-steps': chronology(223, '传为220—226年', '曹魏黄初年间 · 本事有争议', 'disputed', '七步成诗故事晚出，文本版本亦不一致；若依曹丕召试曹植的传统本事，只能系于黄初年间。'),
  'tao-yuanming-guiyuan-3': chronology(405, '约405年', '东晋义熙元年 · 辞彭泽令后', 'circa', '《归园田居》组诗通常系于陶渊明义熙元年辞彭泽令、返回柴桑田居以后。'),
  'wang-ji-ru-ruoye': chronology(510, '约502—519年', '南朝梁天监年间', 'range', '王籍任职会稽、游若耶溪的具体年份未载，只能按其梁天监年间的官历和交游范围系年。'),
  'wu-jun-mountain-poem': chronology(510, '约502—519年', '南朝梁天监年间', 'period', '《山中杂诗》没有地名、题序或可识别事件，现只能置于吴均天监年间的主要诗歌创作期。'),
  'xue-daoheng-homecoming': chronology(585, '585年正月初七', '开皇五年 · 出使南陈', 'exact', '薛道衡开皇四年出使南陈并羁留建康，次年人日感叹离家已二年，作于开皇五年正月初七。'),
  'sui-anonymous-farewell': chronology(600, '约581—618年', '隋代乐府歌辞 · 年代未定', 'period', '作品以隋代无名氏歌辞传世，没有作者、送别对象和题序，无法缩小到隋代某一帝年。'),
  'yang-guang-spring-river': chronology(605, '约605—610年', '大业初年 · 江都曲辞', 'range', '杨广创制《春江花月夜》曲题与早期巡幸江都相联，现无材料精确到某次江行或某一天。'),
  'li-yu-yu-mei-ren': chronology(978, '约978年', '宋太平兴国三年 · 汴京囚居', 'circa', '词作于李煜降宋后囚居汴京的末期，传统常与978年七夕及随后被鸩本事相联，但触发事件仍有争议。'),
  'li-yu-xiang-jian-huan': chronology(977, '约975—978年', '降宋以后 · 汴京囚居', 'range', '亡国幽囚之意与李煜975年降宋后的处境相合，作品没有自题，无法进一步确定年份。'),
  'li-jing-huanxisha': chronology(952, '约943—961年', '南唐保大至中兴年间', 'period', '词无题序纪年，只能按李璟在位及金陵宫廷创作时期系年，具体楼阁与年月均不可考。'),
  'su-shi-shui-diao-ge-tou': chronology(1076, '1076年中秋', '熙宁九年 · 丙辰', 'exact', '词序明记“丙辰中秋，欢饮达旦”，苏轼当时知密州，作品可精确到1076年中秋。'),
  'yang-shen-linjiangxian': chronology(1540, '约1524—1559年', '嘉靖年间 · 谪滇以后', 'range', '此词收入杨慎谪滇后所作《廿一史弹词》，可定在1524年流放云南至去世前，具体编写年未定。'),
  'yuan-mei-moss': chronology(1765, '约1750—1797年', '乾隆年间 · 随园时期', 'range', '《苔》没有自题纪年；袁枚1749年辞官后长期居随园写作，只能按其随园诗期给出范围。'),
}

const curatedPoems: UndatedPoem[] = [
  {
    id: 'shijing-guan-ju', title: '关雎', author: '佚名', dynasty: 'pre-qin',
    year: -900, yearLabel: '约前9世纪', eraLabel: '西周诗歌',
    lines: [
      '关关雎鸠，在河之洲。窈窕淑女，君子好逑。',
      '参差荇菜，左右流之。窈窕淑女，寤寐求之。',
      '求之不得，寤寐思服。悠哉悠哉，辗转反侧。',
      '参差荇菜，左右采之。窈窕淑女，琴瑟友之。',
      '参差荇菜，左右芼之。窈窕淑女，钟鼓乐之。',
    ],
    longitude: 108.82, latitude: 34.18, placeId: 'zhou-fenghao', placeName: '丰镐',
    relation: 'associated', confidence: 'low',
    evidence: '《关雎》所属《周南》的采集范围历来有争论；以西周礼乐中心丰镐作传承关联地，不把坐标解释成诗中河洲。',
    sourceLabel: '《诗经》· 开放古籍校订', sourceUrl,
    accent: '#d6b878', visualEffect: 'river-mist', visualEffectLabel: '河洲 · 荇菜',
  },
  {
    id: 'shijing-shi-wei', title: '式微', author: '佚名', dynasty: 'pre-qin',
    year: -750, yearLabel: '约前8世纪', eraLabel: '春秋诗歌',
    lines: [
      '式微，式微，胡不归？微君之故，胡为乎中露？',
      '式微，式微，胡不归？微君之躬，胡为乎泥中？',
    ],
    longitude: 114.1, latitude: 35.62, placeId: 'chaoge', placeName: '朝歌',
    relation: 'associated', confidence: 'low',
    evidence: '《式微》收入《邶风》，传统经学将邶地纳入卫国故域；以卫都朝歌作篇章关联，具体泥中劳作地不可考。',
    sourceLabel: '《诗经》· 开放古籍校订', sourceUrl,
    accent: '#a9bb92', visualEffect: 'morning-rain', visualEffectLabel: '中露 · 归意',
  },
  {
    id: 'jing-ke-yishui-song', title: '易水歌', author: '荆轲', dynasty: 'pre-qin',
    year: -227, yearLabel: '前227', eraLabel: '战国末年',
    lines: ['风萧萧兮易水寒，壮士一去兮不复还。'],
    longitude: 115.5, latitude: 39.35, placeId: 'yishui', placeName: '燕地 · 易水',
    relation: 'setting', confidence: 'medium',
    evidence: '据《战国策》叙事，歌辞发生于荆轲渡易水赴秦的送别场景；作者归属沿用传统题署。',
    sourceLabel: '《战国策》传统文本 · 开放古籍校订', sourceUrl,
    accent: '#91a9b7', visualEffect: 'river-flight', visualEffectLabel: '寒水 · 长风',
  },
  {
    id: 'liu-bang-da-feng-ge', title: '大风歌', author: '刘邦', dynasty: 'han',
    year: -195, yearLabel: '前195', eraLabel: '汉高祖十二年',
    lines: ['大风起兮云飞扬。', '威加海内兮归故乡。', '安得猛士兮守四方！'],
    longitude: 116.94, latitude: 34.73, placeId: 'pei-song-wind-terrace', placeName: '沛县 · 沛宫歌风台',
    relation: 'composed_at', confidence: 'high',
    evidence: '《史记》《汉书》均记刘邦平定英布后还乡置酒，作歌于沛。',
    sourceLabel: '汉诗 · 开放古籍校订', sourceUrl,
    accent: '#cf9b62', visualEffect: 'cloud-crane', visualEffectLabel: '大风 · 归乡',
  },
  {
    id: 'han-yuefu-jiangnan', title: '江南', author: '汉乐府', dynasty: 'han',
    year: -100, yearLabel: '约前1世纪', eraLabel: '汉代乐府',
    lines: [
      '江南可采莲，莲叶何田田。鱼戏莲叶间。',
      '鱼戏莲叶东，鱼戏莲叶西，鱼戏莲叶南，鱼戏莲叶北。',
    ],
    longitude: 108.86, latitude: 34.3, placeId: 'han-changan-yuefu', placeName: '汉长安城 · 乐府采录署',
    relation: 'associated', confidence: 'low',
    evidence: '汉乐府《江南》只给出广义采莲水景，无法落实为某一湖泊；以长安乐府机构作文本采录关联地，不虚构采莲现场。',
    sourceLabel: '《乐府诗集》· 开放古籍校订', sourceUrl,
    accent: '#72b9a8', visualEffect: 'river-flight', visualEffectLabel: '莲叶 · 游鱼',
  },
  {
    id: 'han-yuefu-shang-ye', title: '上邪', author: '汉乐府', dynasty: 'han',
    year: 50, yearLabel: '约1世纪', eraLabel: '汉代乐府',
    lines: [
      '上邪！我欲与君相知，长命无绝衰。',
      '山无陵，江水为竭，冬雷震震，夏雨雪，天地合，乃敢与君绝！',
    ],
    longitude: 108.86, latitude: 34.3, placeId: 'han-changan-yuefu', placeName: '汉长安城 · 乐府采录署',
    relation: 'associated', confidence: 'low',
    evidence: '《上邪》收入汉乐府《鼓吹曲辞》，具体发生地不可考；以汉长安城乐府采录署作制度性锚点，不把誓言中的山江当作现实地点。',
    sourceLabel: '《乐府诗集》· 开放古籍校订', sourceUrl,
    accent: '#cb806d', visualEffect: 'petals-embers', visualEffectLabel: '山盟 · 天地',
  },
  {
    id: 'cao-zhi-seven-steps', title: '七步诗', author: '曹植', dynasty: 'wei-jin',
    year: 226, yearLabel: '约226', eraLabel: '曹魏黄初年间',
    lines: ['煮豆持作羹，漉菽以为汁。', '萁在釜下燃，豆在釜中泣。', '本自同根生，相煎何太急？'],
    longitude: 112.6, latitude: 34.73, placeId: 'luoyang-wei-palace', placeName: '洛阳 · 曹魏宫城',
    relation: 'associated', confidence: 'low',
    evidence: '七步成诗故事与文本版本均有争议；若依曹丕召试曹植的传统本事，事件应系曹魏洛阳宫城，不应落在此前的邺城政治中心。',
    sourceLabel: '魏晋诗 · 传统题署校订', sourceUrl,
    accent: '#d18b6d', visualEffect: 'petals-embers', visualEffectLabel: '豆萁 · 釜火',
  },
  {
    id: 'tao-yuanming-guiyuan-3', title: '归园田居·其三', author: '陶渊明', dynasty: 'wei-jin',
    year: 405, yearLabel: '约405', eraLabel: '东晋义熙元年',
    lines: [
      '种豆南山下，草盛豆苗稀。', '晨兴理荒秽，带月荷锄归。',
      '道狭草木长，夕露沾我衣。', '衣沾不足惜，但使愿无违。',
    ],
    longitude: 115.88, latitude: 29.46, placeId: 'chaisang-shangjingli', placeName: '柴桑 · 上京里南村田居',
    relation: 'composed_at', confidence: 'medium',
    evidence: '《归园田居》组诗作于辞彭泽令归田后；地图细化到柴桑上京里、庐山南麓的传统田居范围，南山仍按庐山传统解释。',
    sourceLabel: '《陶渊明集》· 开放古籍校订', sourceUrl,
    accent: '#8eb17d', visualEffect: 'moon-fire', visualEffectLabel: '南山 · 荷锄',
  },
  {
    id: 'tao-yuanming-drinking-5', title: '饮酒·其五', author: '陶渊明', dynasty: 'wei-jin',
    year: 417, yearLabel: '约417', eraLabel: '东晋义熙年间',
    lines: [
      '结庐在人境，而无车马喧。', '问君何能尔？心远地自偏。',
      '采菊东篱下，悠然见南山。', '山气日夕佳，飞鸟相与还。',
      '此中有真意，欲辨已忘言。',
    ],
    longitude: 115.88, latitude: 29.46, placeId: 'chaisang', placeName: '柴桑 · 庐山南麓',
    relation: 'composed_at', confidence: 'medium',
    evidence: '作品属于归隐柴桑时期；“南山”所指仍有讨论，地图采用庐山南麓传统解释。',
    sourceLabel: '《陶渊明集》· 开放古籍校订', sourceUrl,
    accent: '#c6a56c', visualEffect: 'cloud-crane', visualEffectLabel: '东篱 · 飞鸟',
  },
  {
    id: 'northern-folk-chile-song', title: '敕勒歌', author: '北朝民歌', dynasty: 'southern-northern',
    year: 546, yearLabel: '约546', eraLabel: '东魏武定年间',
    lines: ['敕勒川，阴山下。', '天似穹庐，笼盖四野。', '天苍苍，野茫茫。风吹草低见牛羊。'],
    longitude: 111.45, latitude: 40.82, placeId: 'chile-plain', placeName: '阴山 · 敕勒川',
    relation: 'setting', confidence: 'medium',
    evidence: '文本明确指向阴山下的敕勒川；现代具体范围有多种说法，坐标取土默川平原。',
    sourceLabel: '《乐府诗集》· 开放古籍校订', sourceUrl,
    accent: '#9cb6a1', visualEffect: 'cloud-crane', visualEffectLabel: '穹庐 · 草野',
  },
  {
    id: 'wang-ji-ru-ruoye', title: '入若耶溪', author: '王籍', dynasty: 'southern-northern',
    year: 510, yearLabel: '约510', eraLabel: '南朝梁天监年间',
    lines: [
      '艅艎何泛泛，空水共悠悠。', '阴霞生远岫，阳景逐回流。',
      '蝉噪林逾静，鸟鸣山更幽。', '此地动归念，长年悲倦游。',
    ],
    longitude: 120.63, latitude: 29.88, placeId: 'ruoye-creek', placeName: '会稽 · 若耶溪',
    relation: 'setting', confidence: 'high',
    evidence: '诗题直接标示若耶溪，传统定位在今绍兴东南一带。',
    sourceLabel: '南朝诗 · 开放古籍校订', sourceUrl,
    accent: '#78afa7', visualEffect: 'river-mist', visualEffectLabel: '空水 · 蝉鸣',
  },
  {
    id: 'wu-jun-mountain-poem', title: '山中杂诗', author: '吴均', dynasty: 'southern-northern',
    year: 510, yearLabel: '约510', eraLabel: '南朝梁天监年间',
    lines: ['山际见来烟，竹中窥落日。', '鸟向檐上飞，云从窗里出。'],
    longitude: 119.58, latitude: 30.64, placeId: 'anji-zhangwu', placeName: '故鄣 · 鄣吴镇吴均故里',
    relation: 'associated', confidence: 'low',
    evidence: '《山中杂诗》没有可识别的山名或居所；以吴均故鄣籍贯所对应的安吉鄣吴镇作传记锚点，不把该故里断言成诗中山居。',
    sourceLabel: '南朝诗 · 开放古籍校订', sourceUrl,
    accent: '#8ba98c', visualEffect: 'morning-rain', visualEffectLabel: '竹烟 · 落日',
  },
  {
    id: 'xue-daoheng-homecoming', title: '人日思归', author: '薛道衡', dynasty: 'sui',
    year: 589, yearLabel: '约589', eraLabel: '开皇九年前后',
    lines: ['入春才七日，离家已二年。', '人归落雁后，思发在花前。'],
    longitude: 118.78, latitude: 32.06, placeId: 'jiankang-envoy', placeName: '建康 · 南陈鸿胪客馆',
    relation: 'composed_at', confidence: 'medium',
    evidence: '《人日思归》作于薛道衡出使南陈、羁留建康期间；使馆确址不可复原，故以建康城作中置信写作地。',
    sourceLabel: '隋诗 · 开放古籍校订', sourceUrl,
    accent: '#a8ba88', visualEffect: 'cloud-crane', visualEffectLabel: '落雁 · 春花',
  },
  {
    id: 'sui-anonymous-farewell', title: '送别', author: '隋代佚名', dynasty: 'sui',
    year: 600, yearLabel: '约600', eraLabel: '隋代',
    lines: ['杨柳青青著地垂，杨花漫漫搅天飞。', '柳条折尽花飞尽，借问行人归不归？'],
    longitude: 108.94, latitude: 34.26, placeId: 'sui-daxing-yuefu', placeName: '大兴城 · 乐府清商署',
    relation: 'associated', confidence: 'low',
    evidence: '隋代佚名《送别》没有城、河或驿站名称；以大兴城乐府传承机构作文本关联地，不虚构折柳现场。',
    sourceLabel: '隋诗 · 传统题署校订', sourceUrl,
    accent: '#a8c989', visualEffect: 'morning-rain', visualEffectLabel: '杨柳 · 飞花',
  },
  {
    id: 'yang-guang-spring-river', title: '春江花月夜·其一', author: '杨广', dynasty: 'sui',
    year: 605, yearLabel: '约605', eraLabel: '大业初年',
    lines: ['暮江平不动，春花满正开。', '流波将月去，潮水带星来。'],
    longitude: 119.42, latitude: 32.39, placeId: 'jiangdu-palace', placeName: '江都宫 · 临江殿',
    relation: 'associated', confidence: 'medium',
    evidence: '杨广《春江花月夜·其一》与其巡幸江都、创制曲题相联；以扬州江都宫作创作语境关联，诗中江岸仍不作唯一断定。',
    sourceLabel: '隋诗 · 开放古籍校订', sourceUrl,
    accent: '#7eaebe', visualEffect: 'moon-fire', visualEffectLabel: '春江 · 星潮',
  },
  {
    id: 'du-fu-chun-wang', title: '春望', author: '杜甫', dynasty: 'tang',
    year: 757, yearLabel: '757', eraLabel: '至德二载',
    lines: ['国破山河在，城春草木深。', '感时花溅泪，恨别鸟惊心。', '烽火连三月，家书抵万金。', '白头搔更短，浑欲不胜簪。'],
    longitude: 108.94, latitude: 34.34, placeId: 'changan', placeName: '长安',
    relation: 'composed_at', confidence: 'high', evidence: '作于唐肃宗至德二载，安史之乱中的长安。',
    sourceLabel: '《全唐诗》· 概念版编辑记录', sourceUrl,
    accent: '#e6ad62', visualEffect: 'petals-embers', visualEffectLabel: '花影 · 烽烟',
  },
  {
    id: 'li-bai-baidi', title: '早发白帝城', author: '李白', dynasty: 'tang',
    year: 759, yearLabel: '759', eraLabel: '乾元二年',
    lines: ['朝辞白帝彩云间，千里江陵一日还。', '两岸猿声啼不住，轻舟已过万重山。'],
    longitude: 109.57, latitude: 31.05, placeId: 'baidicheng', placeName: '白帝城',
    relation: 'route', confidence: 'high', evidence: '诗题和首句均指向从白帝城启程的行旅场景。',
    sourceLabel: '《全唐诗》· 概念版编辑记录', sourceUrl,
    accent: '#7ec9c3', visualEffect: 'river-flight', visualEffectLabel: '彩云 · 轻舟',
  },
  {
    id: 'zhang-ji-fengqiao', title: '枫桥夜泊', author: '张继', dynasty: 'tang',
    year: 756, yearLabel: '约756', eraLabel: '约至德元载',
    lines: ['月落乌啼霜满天，江枫渔火对愁眠。', '姑苏城外寒山寺，夜半钟声到客船。'],
    longitude: 120.57, latitude: 31.31, placeId: 'suzhou-fengqiao', placeName: '姑苏 · 枫桥寒山寺',
    relation: 'setting', confidence: 'high', evidence: '诗题与“姑苏城外寒山寺”共同确定作品的空间场景。',
    sourceLabel: '《全唐诗》· 概念版编辑记录', sourceUrl,
    accent: '#78a4c5', visualEffect: 'moon-fire', visualEffectLabel: '月落 · 渔火',
  },
  {
    id: 'meng-haoran-jiande', title: '宿建德江', author: '孟浩然', dynasty: 'tang',
    year: 730, yearLabel: '约730', eraLabel: '约开元十八年',
    lines: ['移舟泊烟渚，日暮客愁新。', '野旷天低树，江清月近人。'],
    longitude: 119.28, latitude: 29.48, placeId: 'jiande-river', placeName: '建德江',
    relation: 'setting', confidence: 'high', evidence: '诗题直接指向建德江夜泊场景，正文的烟渚、江清与月色共同支持这一定位。',
    sourceLabel: '《全唐诗》· 概念版编辑记录', sourceUrl,
    accent: '#82b8a1', visualEffect: 'river-mist', visualEffectLabel: '烟渚 · 江月',
  },
  {
    id: 'wang-zhihuan-guanquelou', title: '登鹳雀楼', author: '王之涣', dynasty: 'tang',
    year: 724, yearLabel: '约724', eraLabel: '约开元十二年',
    lines: ['白日依山尽，黄河入海流。', '欲穷千里目，更上一层楼。'],
    longitude: 110.31, latitude: 34.84, placeId: 'puzhou-guanquelou', placeName: '蒲州 · 鹳雀楼',
    relation: 'setting', confidence: 'medium', evidence: '按传统题解定位于蒲州鹳雀楼，以作品场景呈现。',
    sourceLabel: '《全唐诗》· 概念版编辑记录', sourceUrl,
    accent: '#d6ca86', visualEffect: 'sun-river', visualEffectLabel: '白日 · 黄河',
  },
  {
    id: 'cui-hao-huanghelou', title: '黄鹤楼', author: '崔颢', dynasty: 'tang',
    year: 725, yearLabel: '约725', eraLabel: '约开元十三年',
    lines: ['昔人已乘黄鹤去，此地空余黄鹤楼。', '黄鹤一去不复返，白云千载空悠悠。', '晴川历历汉阳树，芳草萋萋鹦鹉洲。', '日暮乡关何处是？烟波江上使人愁。'],
    longitude: 114.3, latitude: 30.55, placeId: 'wuhan-huanghelou', placeName: '鄂州 · 黄鹤楼',
    relation: 'setting', confidence: 'high', evidence: '诗题及全诗景物均以黄鹤楼和江上视野为中心。',
    sourceLabel: '《全唐诗》· 概念版编辑记录', sourceUrl,
    accent: '#c89b66', visualEffect: 'cloud-crane', visualEffectLabel: '黄鹤 · 白云',
  },
  {
    id: 'li-bai-lushan', title: '望庐山瀑布', author: '李白', dynasty: 'tang',
    year: 725, yearLabel: '约725', eraLabel: '约开元十三年',
    lines: ['日照香炉生紫烟，遥看瀑布挂前川。', '飞流直下三千尺，疑是银河落九天。'],
    longitude: 115.98, latitude: 29.55, placeId: 'lushan', placeName: '庐山',
    relation: 'setting', confidence: 'high', evidence: '诗题和香炉峰、瀑布意象指向庐山游观场景。',
    sourceLabel: '《全唐诗》· 概念版编辑记录', sourceUrl,
    accent: '#9d8bc0', visualEffect: 'waterfall', visualEffectLabel: '紫烟 · 银河',
  },
  {
    id: 'wang-wei-weicheng', title: '送元二使安西', author: '王维', dynasty: 'tang',
    year: 740, yearLabel: '约740', eraLabel: '约开元二十八年',
    lines: ['渭城朝雨浥轻尘，客舍青青柳色新。', '劝君更尽一杯酒，西出阳关无故人。'],
    longitude: 108.71, latitude: 34.34, placeId: 'weicheng', placeName: '渭城',
    relation: 'setting', confidence: 'medium', evidence: '首句明确给出送别场景；具体写作地点仍需版本考证。',
    sourceLabel: '《全唐诗》· 概念版编辑记录', sourceUrl,
    accent: '#b2c978', visualEffect: 'morning-rain', visualEffectLabel: '朝雨 · 柳色',
  },
  {
    id: 'li-yu-yu-mei-ren', title: '虞美人', author: '李煜', dynasty: 'five-dynasties',
    year: 978, yearLabel: '约978', eraLabel: '宋太平兴国三年',
    lines: [
      '春花秋月何时了？往事知多少。', '小楼昨夜又东风，故国不堪回首月明中。',
      '雕栏玉砌应犹在，只是朱颜改。', '问君能有几多愁？恰似一江春水向东流。',
    ],
    longitude: 114.31, latitude: 34.8, placeId: 'kaifeng-captivity', placeName: '汴京 · 违命侯宅',
    relation: 'composed_at', confidence: 'medium',
    evidence: '传统系于李煜降宋后汴京囚居时期；确切作年与触发事件仍有讨论。',
    sourceLabel: '《南唐二主词》· 开放古籍校订', sourceUrl,
    accent: '#718fb4', visualEffect: 'river-flight', visualEffectLabel: '故国 · 春水',
  },
  {
    id: 'li-yu-xiang-jian-huan', title: '相见欢', author: '李煜', dynasty: 'five-dynasties',
    year: 977, yearLabel: '约977', eraLabel: '降宋以后',
    lines: ['无言独上西楼，月如钩。寂寞梧桐深院锁清秋。', '剪不断，理还乱，是离愁。别是一般滋味在心头。'],
    longitude: 114.31, latitude: 34.8, placeId: 'kaifeng-captivity', placeName: '汴京 · 违命侯宅',
    relation: 'composed_at', confidence: 'medium', evidence: '一般系于李煜降宋后的汴京生活；具体楼院无法确定。',
    sourceLabel: '《南唐二主词》· 开放古籍校订', sourceUrl,
    accent: '#9d91b6', visualEffect: 'moon-fire', visualEffectLabel: '西楼 · 清秋',
  },
  {
    id: 'li-jing-huanxisha', title: '摊破浣溪沙', author: '李璟', dynasty: 'five-dynasties',
    year: 955, yearLabel: '约955', eraLabel: '南唐中主时期',
    lines: [
      '菡萏香销翠叶残，西风愁起绿波间。还与韶光共憔悴，不堪看。',
      '细雨梦回鸡塞远，小楼吹彻玉笙寒。多少泪珠何限恨，倚栏干。',
    ],
    longitude: 118.78, latitude: 32.06, placeId: 'jinling-southern-tang', placeName: '金陵 · 南唐宫城澄心堂',
    relation: 'associated', confidence: 'low',
    evidence: '词中空间由闺阁意象构成，无法唯一定位；以李璟活动的金陵南唐宫城澄心堂作创作语境锚点，不把词中小楼认作该建筑。',
    sourceLabel: '《南唐二主词》· 开放古籍校订', sourceUrl,
    accent: '#8fa89a', visualEffect: 'morning-rain', visualEffectLabel: '细雨 · 玉笙',
  },
  {
    id: 'su-shi-shui-diao-ge-tou', title: '水调歌头', author: '苏轼', dynasty: 'song',
    year: 1076, yearLabel: '1076', eraLabel: '熙宁九年',
    lines: [
      '明月几时有？把酒问青天。不知天上宫阙，今夕是何年。',
      '我欲乘风归去，又恐琼楼玉宇，高处不胜寒。起舞弄清影，何似在人间。',
      '转朱阁，低绮户，照无眠。不应有恨，何事长向别时圆？',
      '人有悲欢离合，月有阴晴圆缺，此事古难全。',
      '但愿人长久，千里共婵娟。',
    ],
    longitude: 119.41, latitude: 35.99, placeId: 'mizhou', placeName: '密州 · 诸城',
    relation: 'composed_at', confidence: 'high',
    evidence: '词序明记丙辰中秋作；苏轼当时知密州，今山东诸城。',
    sourceLabel: '《东坡乐府》· 开放古籍校订', sourceUrl,
    accent: '#c6c08c', visualEffect: 'moon-fire', visualEffectLabel: '明月 · 婵娟',
  },
  {
    id: 'su-shi-xilin-wall', title: '题西林壁', author: '苏轼', dynasty: 'song',
    year: 1084, yearLabel: '1084', eraLabel: '元丰七年',
    lines: ['横看成岭侧成峰，远近高低各不同。', '不识庐山真面目，只缘身在此山中。'],
    longitude: 115.97, latitude: 29.58, placeId: 'lushan-xilin', placeName: '庐山 · 西林寺',
    relation: 'composed_at', confidence: 'high', evidence: '诗题直接标示西林寺墙壁，作于苏轼自黄州赴汝州途中游庐山。',
    sourceLabel: '《苏轼诗集》· 开放古籍校订', sourceUrl,
    accent: '#8ca6a0', visualEffect: 'river-mist', visualEffectLabel: '层岭 · 云岚',
  },
  {
    id: 'li-qingzhao-ru-meng-ling', title: '如梦令', author: '李清照', dynasty: 'song',
    year: 1100, yearLabel: '约1100', eraLabel: '北宋后期',
    lines: ['常记溪亭日暮，沉醉不知归路。', '兴尽晚回舟，误入藕花深处。', '争渡，争渡，惊起一滩鸥鹭。'],
    longitude: 117.12, latitude: 36.65, placeId: 'jinan-xiting', placeName: '济南 · 溪亭',
    relation: 'associated', confidence: 'low',
    evidence: '“溪亭”具体所在不可确证；以词人早年生活的济南水域作人物关联锚点。',
    sourceLabel: '《漱玉词》· 开放古籍校订', sourceUrl,
    accent: '#86b9ad', visualEffect: 'river-flight', visualEffectLabel: '藕花 · 鸥鹭',
  },
  {
    id: 'ma-zhiyuan-autumn-thoughts', title: '天净沙·秋思', author: '马致远', dynasty: 'yuan',
    year: 1280, yearLabel: '约1280', eraLabel: '元初',
    lines: ['枯藤老树昏鸦，小桥流水人家，古道西风瘦马。', '夕阳西下，断肠人在天涯。'],
    longitude: 116.4, latitude: 39.9, placeId: 'dadu', placeName: '大都 · 马致远故居',
    relation: 'associated', confidence: 'low',
    evidence: '《天净沙·秋思》的古道、小桥和人家是组合意象，无法唯一定位；以马致远大都故居作人物关联，不称其为曲中驿路。',
    sourceLabel: '元曲 · 开放古籍校订', sourceUrl,
    accent: '#bf926a', visualEffect: 'sun-river', visualEffectLabel: '古道 · 夕阳',
  },
  {
    id: 'zhang-yanghao-tongguan', title: '山坡羊·潼关怀古', author: '张养浩', dynasty: 'yuan',
    year: 1329, yearLabel: '1329', eraLabel: '天历二年',
    lines: ['峰峦如聚，波涛如怒，山河表里潼关路。', '望西都，意踌躇。伤心秦汉经行处，宫阙万间都做了土。', '兴，百姓苦；亡，百姓苦。'],
    longitude: 110.25, latitude: 34.54, placeId: 'tongguan', placeName: '潼关',
    relation: 'route', confidence: 'high', evidence: '作于赴陕西赈灾途中，曲题和正文均明确指向潼关。',
    sourceLabel: '元曲 · 开放古籍校订', sourceUrl,
    accent: '#c28e5d', visualEffect: 'sun-river', visualEffectLabel: '峰峦 · 怒涛',
  },
  {
    id: 'wang-mian-ink-plum', title: '墨梅', author: '王冕', dynasty: 'yuan',
    year: 1350, yearLabel: '约1350', eraLabel: '元末',
    lines: ['我家洗砚池头树，朵朵花开淡墨痕。', '不要人夸好颜色，只留清气满乾坤。'],
    longitude: 120.23, latitude: 29.71, placeId: 'zhuji', placeName: '诸暨 · 九里山',
    relation: 'associated', confidence: 'medium',
    evidence: '题画诗与王冕隐居九里山时期相联系；坐标为人物活动地，并非画中树木的可考位置。',
    sourceLabel: '元诗 · 开放古籍校订', sourceUrl,
    accent: '#afb5a9', visualEffect: 'petals-embers', visualEffectLabel: '墨梅 · 清气',
  },
  {
    id: 'yu-qian-lime-song', title: '石灰吟', author: '于谦', dynasty: 'ming',
    year: 1446, yearLabel: '约1446', eraLabel: '明正统年间',
    lines: ['千锤万凿出深山，烈火焚烧若等闲。', '粉骨碎身浑不怕，要留清白在人间。'],
    longitude: 120.16, latitude: 30.25, placeId: 'hangzhou-yuqian', placeName: '杭州 · 于谦故里',
    relation: 'associated', confidence: 'low',
    evidence: '作品具体写作时间、地点缺乏可靠定论；以于谦故里杭州作为人物关联地。',
    sourceLabel: '明诗 · 开放古籍校订', sourceUrl,
    accent: '#d1c7a5', visualEffect: 'petals-embers', visualEffectLabel: '烈火 · 清白',
  },
  {
    id: 'yang-shen-linjiangxian', title: '临江仙', author: '杨慎', dynasty: 'ming',
    year: 1530, yearLabel: '约1530', eraLabel: '嘉靖年间',
    lines: [
      '滚滚长江东逝水，浪花淘尽英雄。是非成败转头空。',
      '青山依旧在，几度夕阳红。',
      '白发渔樵江渚上，惯看秋月春风。一壶浊酒喜相逢。',
      '古今多少事，都付笑谈中。',
    ],
    longitude: 99.16, latitude: 25.12, placeId: 'yongchang-yangshen', placeName: '永昌卫 · 杨慎谪所',
    relation: 'associated', confidence: 'low',
    evidence: '《临江仙》收入杨慎谪滇时期相关著述，具体落笔处不可考；以其长期居留的永昌卫作人物关联，不把词中长江坐标移到云南。',
    sourceLabel: '《廿一史弹词》· 开放古籍校订', sourceUrl,
    accent: '#ad8d68', visualEffect: 'sun-river', visualEffectLabel: '长江 · 夕阳',
  },
  {
    id: 'tang-yin-painted-rooster', title: '画鸡', author: '唐寅', dynasty: 'ming',
    year: 1505, yearLabel: '约1505', eraLabel: '弘治至正德年间',
    lines: ['头上红冠不用裁，满身雪白走将来。', '平生不敢轻言语，一叫千门万户开。'],
    longitude: 120.62, latitude: 31.3, placeId: 'suzhou-taohuawu', placeName: '苏州 · 桃花坞',
    relation: 'associated', confidence: 'medium',
    evidence: '《画鸡》是唐寅题画诗，画面本身不是真实地景；以其居住并从事书画的苏州桃花坞作创作语境关联。',
    sourceLabel: '明诗 · 开放古籍校订', sourceUrl,
    accent: '#d7986c', visualEffect: 'petals-embers', visualEffectLabel: '红冠 · 晨光',
  },
  {
    id: 'gong-zizhen-jihai-5', title: '己亥杂诗·其五', author: '龚自珍', dynasty: 'qing',
    year: 1839, yearLabel: '1839', eraLabel: '道光十九年',
    lines: ['浩荡离愁白日斜，吟鞭东指即天涯。', '落红不是无情物，化作春泥更护花。'],
    longitude: 116.4, latitude: 39.9, placeId: 'beijing', placeName: '京师',
    relation: 'route', confidence: 'high', evidence: '组诗作于辞官离京返乡途中；本首明确写离京东行。',
    sourceLabel: '《己亥杂诗》· 开放古籍校订', sourceUrl,
    accent: '#c88972', visualEffect: 'petals-embers', visualEffectLabel: '落红 · 春泥',
  },
  {
    id: 'zheng-xie-bamboo-rock', title: '竹石', author: '郑燮', dynasty: 'qing',
    year: 1751, yearLabel: '约1751', eraLabel: '乾隆年间',
    lines: ['咬定青山不放松，立根原在破岩中。', '千磨万击还坚劲，任尔东西南北风。'],
    longitude: 119.43, latitude: 32.39, placeId: 'yangzhou', placeName: '扬州',
    relation: 'associated', confidence: 'low',
    evidence: '题画诗具体绘作地点不可考；以郑燮归居扬州、从事书画的文化语境作关联。',
    sourceLabel: '《板桥题画》· 开放古籍校订', sourceUrl,
    accent: '#83a687', visualEffect: 'morning-rain', visualEffectLabel: '青竹 · 岩风',
  },
  {
    id: 'yuan-mei-moss', title: '苔', author: '袁枚', dynasty: 'qing',
    year: 1760, yearLabel: '约1760', eraLabel: '乾隆年间',
    lines: ['白日不到处，青春恰自来。', '苔花如米小，也学牡丹开。'],
    longitude: 118.74, latitude: 32.06, placeId: 'nanjing-suiyuan', placeName: '金陵 · 随园',
    relation: 'associated', confidence: 'medium',
    evidence: '作品确切作年、地点未见自题；以袁枚长期居住写作的金陵随园作人物关联地。',
    sourceLabel: '《小仓山房诗集》· 开放古籍校订', sourceUrl,
    accent: '#a4b87b', visualEffect: 'river-mist', visualEffectLabel: '苔花 · 微光',
  },
]

const schoolPoemByText = new Map(
  schoolPoems.map((poem) => [schoolPoemTextKey(poem), poem]),
)

/**
 * A curated text can be a shorter edition of a school text (for example the
 * school edition keeps a preface). The school edition wins, but the poem
 * keeps its curated position and id so links stay stable.
 */
function schoolEditionOf(poem: UndatedPoem | Poem) {
  const key = schoolPoemTextKey(poem)
  const exact = schoolPoemByText.get(key)
  if (exact) return exact
  return schoolPoems.find((schoolPoem) =>
    schoolPoem.author === poem.author && schoolPoemTextKey(schoolPoem).includes(key))
}

const publishedTextKeys = new Set<string>()

export const poems: Poem[] = [...curatedPoems, ...schoolPoems, ...expandedPoems]
  .map((poem) => {
    const schoolPoem = schoolEditionOf(poem)
    return schoolPoem && schoolPoem !== poem
      ? { ...poem, title: schoolPoem.title, lines: schoolPoem.lines }
      : poem
  })
  .filter((poem) => {
    const key = schoolPoemTextKey(poem)
    if (publishedTextKeys.has(key)) return false
    publishedTextKeys.add(key)
    return true
  })
  .map((poem) => {
    const schoolPoem = schoolPoemByText.get(schoolPoemTextKey(poem))
    const ownChronology = 'datePrecision' in poem ? poem : undefined
    const poemChronology = schoolPoem ?? curatedPoemChronologies[poem.id] ?? ownChronology
    if (!poemChronology) {
      throw new Error(`Missing individual chronology for published poem ${poem.id} (${poem.title})`)
    }
    return {
      ...poem,
      year: poemChronology.year,
      yearLabel: poemChronology.yearLabel,
      eraLabel: poemChronology.eraLabel,
      datePrecision: poemChronology.datePrecision,
      dateEvidence: poemChronology.dateEvidence,
      ...(schoolPoem && {
        longitude: schoolPoem.longitude,
        latitude: schoolPoem.latitude,
        placeId: schoolPoem.placeId,
        placeName: schoolPoem.placeName,
        relation: schoolPoem.relation,
        confidence: schoolPoem.confidence,
        evidence: schoolPoem.evidence,
        sourceLabel: schoolPoem.sourceLabel,
        sourceUrl: schoolPoem.sourceUrl,
        curriculumLevels: schoolPoem.curriculumLevels,
      }),
    }
  })

export const datePrecisionLabels: Record<Poem['datePrecision'], string> = {
  exact: '明确纪年',
  circa: '约年考订',
  range: '可考范围',
  period: '时代约定',
  disputed: '年代有异说',
}

export const defaultPoem = poems.find((poem) => poem.id === 'du-fu-chun-wang') ?? poems[0]

export const relationLabels: Record<Poem['relation'], string> = {
  composed_at: '较可信创作地',
  setting: '作品场景',
  mentioned: '诗中提及',
  route: '行旅节点',
  associated: '人物关联地',
}

export const confidenceLabels: Record<Poem['confidence'], string> = {
  high: '高置信度',
  medium: '待进一步考证',
  low: '低置信度关联',
}
